import { memo, useEffect } from "react"
import { useNavigate, useParams } from "@tanstack/react-router"

import { apiMethodLabel } from "@productlane/lib/src/api-reference/method-label"
import { cn } from "@productlane/lib/src/cn"
import { NucleoFillIcon } from "@productlane/ui"

import type { RouterOutputs } from "@/lib/api"
import { api } from "@/lib/api"
import { useDocNavStore } from "@/stores/docs/docs-nav-store"
import { API_MONO_CLASS } from "./api-reference-env"
import { NavDisclosureChevron } from "./nav-disclosure-chevron"
import { NavLandingRow } from "./nav-landing-row"

type ApiGroup = NonNullable<
	RouterOutputs["apiReference"]["get"]
>["groups"][number]

const ROW_CLASS =
	"flex h-[1.9rem] w-full min-w-0 cursor-default select-none items-center gap-2 rounded-md pl-2 pr-1 text-left text-sm font-medium"

export const METHOD_TEXT: Record<string, string> = {
	GET: "text-blue-700 dark:text-blue-400",
	POST: "text-green-700 dark:text-green-400",
	PUT: "text-amber-700 dark:text-amber-400",
	PATCH: "text-amber-700 dark:text-amber-400",
	DEL: "text-red-700 dark:text-red-400",
}

export const apiNavGroupKey = (tabId: string, groupSlug: string) =>
	`api:${tabId}:${groupSlug}`

/** The API reference's overview row, above the tab's own groups. */
export const ApiReferenceOverviewRow = memo(({ tabId }: { tabId: string }) => {
	const navigate = useNavigate()
	const params = useParams({ strict: false })
	return (
		<NavLandingRow
			icon={
				<NucleoFillIcon icon="BracketsCurly" className="size-3.5 flex-none" />
			}
			label="Endpoints overview"
			isActive={params.tabId === tabId && !params.groupSlug}
			onClick={() =>
				void navigate({ to: "/edit-docs/api/$tabId", params: { tabId } })
			}
		/>
	)
})

/** The spec's endpoint groups, read-only: synced from the spec, so they can't be moved or edited here. */
export const ApiReferenceNavGroups = memo(({ tabId }: { tabId: string }) => {
	const query = api.apiReference.get.useQuery({ tabId })
	const params = useParams({ strict: false })
	const openGroup = useDocNavStore((s) => s.openGroup)
	const groups = query.data?.groups ?? []
	const hidden = new Set(query.data?.state?.hidden ?? [])

	// Opening an endpoint by link or from the overview unfolds its group.
	const currentGroup = params.tabId === tabId ? params.groupSlug : undefined
	useEffect(() => {
		if (currentGroup) openGroup(apiNavGroupKey(tabId, currentGroup))
	}, [currentGroup, tabId, openGroup])

	if (groups.length === 0) return null
	return (
		<div className={cn("flex flex-col gap-px", API_MONO_CLASS)}>
			{groups.map((group) => (
				<ApiNavGroup
					key={group.slug}
					tabId={tabId}
					group={group}
					hidden={hidden}
					activeSlug={
						currentGroup === group.slug ? (params.slug ?? null) : null
					}
				/>
			))}
		</div>
	)
})

const ApiNavGroup = memo(
	({
		tabId,
		group,
		hidden,
		activeSlug,
	}: {
		tabId: string
		group: ApiGroup
		hidden: Set<string>
		activeSlug: string | null
	}) => {
		const navigate = useNavigate()
		const key = apiNavGroupKey(tabId, group.slug)
		const isOpen = useDocNavStore((s) => s.openGroupIds.includes(key))
		const toggleGroup = useDocNavStore((s) => s.toggleGroup)
		const name = group.kind === "webhook" ? "Webhook events" : group.name

		return (
			<div className="flex flex-col">
				<button
					type="button"
					aria-expanded={isOpen}
					onClick={() => toggleGroup(key)}
					className="group flex h-[1.9rem] w-full cursor-default select-none items-center justify-between rounded-md pl-2 pr-1 text-sm hover:bg-gray-100 hover:text-primary dark:hover:bg-gray-800/50"
				>
					<span className="truncate text-left font-medium text-secondary opacity-90 group-hover:text-primary">
						{name}
					</span>
					<NavDisclosureChevron isOpen={isOpen} />
				</button>
				{isOpen && (
					<ul className="list-none space-y-0.5 pb-3 pt-1">
						{group.items.map((item) => {
							const label = apiMethodLabel(item.method, item.kind)
							const isHidden = hidden.has(item.key) || item.specHidden
							const isActive = activeSlug === item.slug
							return (
								<li key={item.key}>
									<button
										type="button"
										title={isHidden ? `${item.title} (hidden)` : item.title}
										onClick={() =>
											void navigate({
												to: "/edit-docs/api/$tabId/$groupSlug/$slug",
												params: {
													tabId,
													groupSlug: group.slug,
													slug: item.slug,
												},
											})
										}
										className={cn(
											ROW_CLASS,
											isActive
												? "bg-gray-100 text-primary dark:bg-gray-800"
												: "text-secondary hover:bg-gray-100 hover:text-primary dark:hover:bg-gray-800/50",
										)}
									>
										<span
											className={cn(
												"w-9 flex-none font-mono text-[10.5px] font-semibold",
												METHOD_TEXT[label] ?? "text-tertiary",
												isHidden && "opacity-50",
											)}
										>
											{label}
										</span>
										<span
											className={cn(
												"min-w-0 flex-1 truncate opacity-90",
												isHidden && "text-tertiary line-through",
											)}
										>
											{item.title}
										</span>
									</button>
								</li>
							)
						})}
					</ul>
				)}
			</div>
		)
	},
)
